import React, { useState, useEffect } from "react";
import { Plus, Edit, Eye, ChevronUp, ChevronDown, Trash2 } from "lucide-react";
import { supabase } from "../lib/supabase";
import { Series as SeriesType } from "../lib/supabase";
import { useAuth } from "../contexts/AuthContext";
import toast from "react-hot-toast";
import { Link } from "react-router-dom";
import { ENTITY_NAMES } from "../constants/entities";
import { formatDaysOfWeek } from "../utils/runs";

interface SeriesProps {
  typeFilter?: "series" | "event" | "camp";
}

// Define the extended series type with runs
interface SeriesWithRuns extends SeriesType {
  runs: {
    id: string;
    name: string;
    start_date: string;
    day_of_week: string[];
    start_time: string;
    active: boolean;
    student_count: number;
    students: any[];
  }[];
}

type SortField = "title" | "currentRuns" | "totalStudents" | "code";
type SortDirection = "asc" | "desc";

const Series: React.FC<SeriesProps> = ({ typeFilter }) => {
  const [series, setSeries] = useState<SeriesWithRuns[]>([]);
  const [loading, setLoading] = useState(true);
  const { userRole } = useAuth();
  const isAdmin = userRole === "admin";
  const [sortField, setSortField] = useState<SortField>("code");
  const [sortDirection, setSortDirection] = useState<SortDirection>("asc");

  // Derive display labels from typeFilter
  const pageTitle =
    typeFilter === "event"
      ? "Events"
      : typeFilter === "camp"
      ? "Camps"
      : ENTITY_NAMES.SERIES;

  const addLabel =
    typeFilter === "event"
      ? "Add Event"
      : typeFilter === "camp"
      ? "Add Camp"
      : `Add ${ENTITY_NAMES.SERIES}`;

  useEffect(() => {
    fetchSeries();
  }, [typeFilter]);

  const fetchSeries = async () => {
    try {
      let query = supabase
        .from("series")
        .select(
          `
          *,
          runs (
            id,
            name,
            start_date,
            day_of_week,
            start_time,
            active,
            students (*)
          )
        `
        )
        .order("code");

      if (typeFilter) {
        query = query.eq("type", typeFilter);
      }

      const { data, error } = await query;

      if (error) throw error;

      const seriesWithRuns = data.map((item: any) => ({
        ...item,
        runs: item.runs
          ? item.runs.map((run: any) => ({
              ...run,
              student_count: run.students?.length || 0,
            }))
          : [],
      }));

      setSeries(seriesWithRuns);
      setLoading(false);
    } catch (error) {
      console.error("Error fetching series:", error);
      toast.error("Failed to fetch series");
      setLoading(false);
    }
  };

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortDirection("asc");
    }
  };

  const handleDelete = async (id: string) => {
    if (
      window.confirm(
        `Are you sure you want to delete this ${ENTITY_NAMES.SERIES}? This will also delete all associated ${ENTITY_NAMES.RUN}s and student enrollments.`
      )
    ) {
      try {
        const { error } = await supabase.from("series").delete().eq("id", id);

        if (error) throw error;

        setSeries(series.filter((s) => s.id !== id));
        toast.success(`${ENTITY_NAMES.SERIES} deleted successfully`);
      } catch (error) {
        console.error(`Error deleting ${ENTITY_NAMES.SERIES}:`, error);
        toast.error(`Failed to delete ${ENTITY_NAMES.SERIES}`);
      }
    }
  };

  const getSortedSeries = () => {
    return [...series].sort((a, b) => {
      const multiplier = sortDirection === "asc" ? 1 : -1;

      switch (sortField) {
        case "code":
          return (
            multiplier *
            ((a as any).code || "").localeCompare((b as any).code || "")
          );
        case "title":
          return multiplier * a.title.localeCompare(b.title);
        case "currentRuns":
          return (
            multiplier *
            (a.runs.filter((run) => run.active).length -
              b.runs.filter((run) => run.active).length)
          );
        case "totalStudents":
          return (
            multiplier *
            (a.runs.reduce((total, run) => total + run.student_count, 0) -
              b.runs.reduce((total, run) => total + run.student_count, 0))
          );
        default:
          return 0;
      }
    });
  };

  const sortedSeries = getSortedSeries();

  const SortIcon = ({ field }: { field: SortField }) => {
    const isCurrentField = sortField === field;
    return (
      <span
        className={`inline-block ml-1 transition-opacity ${
          isCurrentField ? "opacity-100" : "opacity-0 group-hover:opacity-100"
        }`}
      >
        {sortDirection === "asc" && isCurrentField ? (
          <ChevronUp className="h-4 w-4" />
        ) : (
          <ChevronDown className="h-4 w-4" />
        )}
      </span>
    );
  };

  return (
    <div>
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6">
        <h1 className="">{pageTitle}</h1>
        {isAdmin && (
          <Link to="/series/add" className="btn btn-primary mt-4 sm:mt-0">
            <Plus className="h-5 w-5 mr-2 flex-shrink-0" />
            {addLabel}
          </Link>
        )}
      </div>

      <div className="bg-white shadow-sm rounded-lg border border-neutral-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-neutral-200">
            <thead className="bg-neutral-50">
              <tr>
                <th
                  scope="col"
                  className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider cursor-pointer"
                  onClick={() => handleSort("code")}
                >
                  <span className="inline-flex items-center">
                    Code
                    <SortIcon field="code" />
                  </span>
                </th>
                <th
                  className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider cursor-pointer hover:text-neutral-700 group"
                  onClick={() => handleSort("title")}
                >
                  <span className="inline-flex items-center">
                    Title
                    <SortIcon field="title" />
                  </span>
                </th>
                <th
                  className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider cursor-pointer hover:text-neutral-700 group"
                  onClick={() => handleSort("currentRuns")}
                >
                  <span className="inline-flex items-center">
                    Current {ENTITY_NAMES.RUN}s
                    <SortIcon field="currentRuns" />
                  </span>
                </th>
                <th
                  className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider cursor-pointer hover:text-neutral-700 group"
                  onClick={() => handleSort("totalStudents")}
                >
                  <span className="inline-flex items-center">
                    Total Students
                    <SortIcon field="totalStudents" />
                  </span>
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-neutral-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-neutral-200">
              {loading ? (
                Array(6)
                  .fill(0)
                  .map((_, index) => (
                    <tr key={index}>
                      {Array(5)
                        .fill(0)
                        .map((_, cellIndex) => (
                          <td
                            key={cellIndex}
                            className="px-6 py-4 whitespace-nowrap"
                          >
                            <div className="h-4 bg-neutral-200 rounded animate-pulse"></div>
                          </td>
                        ))}
                    </tr>
                  ))
              ) : sortedSeries.length > 0 ? (
                sortedSeries.map((s) => (
                  <tr key={s.id} className="hover:bg-neutral-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="font-medium">
                        <Link to={`/series/${s.id}`} className="">
                          {(s as any).code}
                        </Link>{" "}
                      </div>
                      <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium mt-1 ${
                        (s as any).type === 'event'
                          ? 'bg-amber-100 text-amber-800'
                          : (s as any).type === 'camp'
                          ? 'bg-green-100 text-green-800'
                          : 'bg-blue-100 text-blue-800'
                      }`}>
                        {(s as any).type === 'event' ? 'Event' : (s as any).type === 'camp' ? 'Camp' : 'Series'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm">{s.title}</div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="space-y-2">
                        {s.runs.filter((run) => run.active).length > 0 ? (
                          s.runs
                            .filter((run) => run.active)
                            .map((run) => (
                              <div key={run.id}>
                                <div>
                                  <Link
                                    to={`/runs/${run.id}`}
                                    className="inline"
                                  >
                                    {ENTITY_NAMES.RUN} {run.name}
                                  </Link>{" "}
                                  <span className="text-sm text-neutral-500">
                                    ({run.student_count} students)
                                  </span>
                                </div>
                                <div className="text-sm">
                                  {formatDaysOfWeek(run.day_of_week)} at{" "}
                                  {new Date(
                                    `2000-01-01T${run.start_time}`
                                  ).toLocaleTimeString("en-US", {
                                    hour: "numeric",
                                    minute: "2-digit",
                                  })}
                                </div>
                              </div>
                            ))
                        ) : (
                          <span className="text-sm text-neutral-500">
                            No active {ENTITY_NAMES.RUN}s
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm">
                        {s.runs.reduce(
                          (total, run) => total + run.student_count,
                          0
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex items-center justify-end space-x-2">
                        <Link
                          to={`/series/${s.id}`}
                          className="icon-view"
                          title="View series details"
                        >
                          <Eye className="h-5 w-5" />
                        </Link>
                        {isAdmin && (
                          <>
                            <Link
                              to={`/series/${s.id}/edit`}
                              className="icon-edit"
                              title="Edit series"
                            >
                              <Edit className="h-5 w-5" />
                            </Link>
                            <button
                              onClick={() => handleDelete(s.id)}
                              className="icon-delete"
                              title="Delete series"
                            >
                              <Trash2 className="h-5 w-5" />
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td
                    colSpan={5}
                    className="px-6 py-4 text-center text-neutral-500"
                  >
                    No series found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default Series;
